import {usePersonalData} from 'presentation/hooks';
import {useEffect, useState} from 'react';
import {
	FAVORITE_ADDRESS_TUTORIAL_STAGES,
	TUTORIAL_PROGRESS_EVENT,
	getStorageKey,
} from '../constants';
import type {
	FavoriteAddressTutorialStage,
	FavoriteAddressTutorialState,
	FavoriteAddressTutorialStep,
} from '../types';
import {getCurrentTutorialStage} from '../util/getCurrentTutorialStage';
import {getStageForStep} from '../util/getStageForStep';
import {getTargetSelector} from '../util/getTargetSelector';
import {useWaitForElement} from './useWaitForElement';
import {notifyTutorialProgressChange} from '../util/notifyTutorialProgressChange';

export function useFavoriteAddressTutorial(
	step: FavoriteAddressTutorialStep,
	isBlocked = false,
): FavoriteAddressTutorialState {
	const {accountData} = usePersonalData();
	const loggedUserCode = accountData?.logged_user?.cdUsuario;
	const storageKey = getStorageKey(step, loggedUserCode);
	const [currentStage, setCurrentStage] = useState<FavoriteAddressTutorialStage>(() =>
		getCurrentTutorialStage(loggedUserCode),
	);
	const targetSelector = isBlocked ? '' : getTargetSelector(step);
	const targetReady = useWaitForElement(targetSelector);
	const isActiveStep =
		!isBlocked &&
		currentStage !== FAVORITE_ADDRESS_TUTORIAL_STAGES.COMPLETED &&
		currentStage === getStageForStep(step);

	useEffect(() => {
		const syncCurrentStage = () => {
			const nextStage = getCurrentTutorialStage(loggedUserCode);

			setCurrentStage(previousStage =>
				previousStage === nextStage ? previousStage : nextStage,
			);
		};

		syncCurrentStage();

		window.addEventListener(TUTORIAL_PROGRESS_EVENT, syncCurrentStage);
		window.addEventListener('storage', syncCurrentStage);

		return () => {
			window.removeEventListener(TUTORIAL_PROGRESS_EVENT, syncCurrentStage);
			window.removeEventListener('storage', syncCurrentStage);
		};
	}, [loggedUserCode]);

	const persistStepProgress = () => {
		if (localStorage.getItem(storageKey) === 'true') {
			return;
		}

		localStorage.setItem(storageKey, 'true');
		notifyTutorialProgressChange();
	};

	return {
		currentStage,
		storageKey,
		targetSelector,
		targetReady,
		isActiveStep,
		isBlocked,
		persistStepProgress,
	};
}
